/**
 * Icon Performance Monitor
 * Tracks and reports icon loading performance metrics
 */

class IconPerformanceMonitor {
  constructor() {
    this.metrics = {
      totalIcons: 0,
      loadedIcons: 0,
      failedIcons: 0,
      totalLoadTime: 0,
      averageLoadTime: 0,
      largestIcon: { name: '', size: 0 },
      loadTimes: [],
      errors: []
    };
    
    this.loadStartTimes = new Map();
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  /**
   * Start tracking an icon load
   * @param {string} iconName - Name of the icon
   * @param {string} iconSrc - Source URL of the icon
   */
  startLoad(iconName, iconSrc) {
    if (!this.isEnabled) return;
    
    this.loadStartTimes.set(iconSrc, {
      name: iconName,
      startTime: performance.now()
    });
    
    this.metrics.totalIcons++;
  }

  /**
   * Record successful icon load
   * @param {string} iconSrc - Source URL of the icon
   * @param {number} fileSize - File size in bytes (optional)
   */
  recordLoad(iconSrc, fileSize = 0) {
    if (!this.isEnabled) return;
    
    const loadData = this.loadStartTimes.get(iconSrc);
    if (!loadData) return;
    
    const loadTime = performance.now() - loadData.startTime;
    
    this.metrics.loadedIcons++;
    this.metrics.totalLoadTime += loadTime;
    this.metrics.averageLoadTime = this.metrics.totalLoadTime / this.metrics.loadedIcons;
    this.metrics.loadTimes.push({
      name: loadData.name,
      src: iconSrc,
      loadTime,
      fileSize
    });
    
    // Track largest icon
    if (fileSize > this.metrics.largestIcon.size) {
      this.metrics.largestIcon = {
        name: loadData.name,
        size: fileSize
      };
    }
    
    this.loadStartTimes.delete(iconSrc);
  }

  /**
   * Record failed icon load
   * @param {string} iconSrc - Source URL of the icon
   * @param {string} error - Error message
   */
  recordError(iconSrc, error) {
    if (!this.isEnabled) return;
    
    const loadData = this.loadStartTimes.get(iconSrc);
    if (!loadData) return;
    
    this.metrics.failedIcons++;
    this.metrics.errors.push({
      name: loadData.name,
      src: iconSrc,
      error,
      timestamp: new Date().toISOString()
    });
    
    this.loadStartTimes.delete(iconSrc);
  }

  /**
   * Get current performance metrics
   * @returns {object} - Performance metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      successRate: this.metrics.totalIcons > 0 
        ? ((this.metrics.loadedIcons / this.metrics.totalIcons) * 100).toFixed(2) + '%'
        : '0%',
      failureRate: this.metrics.totalIcons > 0
        ? ((this.metrics.failedIcons / this.metrics.totalIcons) * 100).toFixed(2) + '%'
        : '0%'
    };
  }

  /**
   * Get performance recommendations
   * @returns {string[]} - Array of recommendations
   */
  getRecommendations() {
    const recommendations = [];
    const metrics = this.getMetrics();
    
    if (parseFloat(metrics.failureRate) > 5) {
      recommendations.push('High failure rate detected. Check icon file paths and network connectivity.');
    }
    
    if (metrics.averageLoadTime > 100) {
      recommendations.push('Average load time is high. Consider optimizing icon file sizes.');
    }
    
    if (metrics.largestIcon.size > 500000) { // 500KB
      recommendations.push(`Large icon detected: ${metrics.largestIcon.name} (${this.formatBytes(metrics.largestIcon.size)}). Consider compression.`);
    }
    
    if (metrics.loadTimes.length > 0) {
      const slowIcons = metrics.loadTimes.filter(icon => icon.loadTime > 200);
      if (slowIcons.length > 0) {
        recommendations.push(`Slow loading icons: ${slowIcons.map(icon => icon.name).join(', ')}`);
      }
    }
    
    return recommendations;
  }

  /**
   * Format bytes to human readable format
   * @param {number} bytes - Bytes to format
   * @returns {string} - Formatted string
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate performance report
   * @returns {string} - Formatted performance report
   */
  generateReport() {
    const metrics = this.getMetrics();
    const recommendations = this.getRecommendations();
    
    let report = '\n=== ICON PERFORMANCE REPORT ===\n';
    report += `Total Icons: ${metrics.totalIcons}\n`;
    report += `Loaded Successfully: ${metrics.loadedIcons}\n`;
    report += `Failed to Load: ${metrics.failedIcons}\n`;
    report += `Success Rate: ${metrics.successRate}\n`;
    report += `Average Load Time: ${metrics.averageLoadTime.toFixed(2)}ms\n`;
    
    if (metrics.largestIcon.name) {
      report += `Largest Icon: ${metrics.largestIcon.name} (${this.formatBytes(metrics.largestIcon.size)})\n`;
    }
    
    if (recommendations.length > 0) {
      report += '\n=== RECOMMENDATIONS ===\n';
      recommendations.forEach(rec => {
        report += `• ${rec}\n`;
      });
    }
    
    if (metrics.errors.length > 0) {
      report += '\n=== ERRORS ===\n';
      metrics.errors.forEach(error => {
        report += `• ${error.name}: ${error.error}\n`;
      });
    }
    
    return report;
  }

  /**
   * Log performance report to console
   */
  logReport() {
    if (!this.isEnabled) return;
    console.log(this.generateReport());
  }

  /**
   * Reset all metrics
   */
  reset() {
    this.metrics = {
      totalIcons: 0,
      loadedIcons: 0,
      failedIcons: 0,
      totalLoadTime: 0,
      averageLoadTime: 0,
      largestIcon: { name: '', size: 0 },
      loadTimes: [],
      errors: []
    };
    this.loadStartTimes.clear();
  }

  /**
   * Enable or disable monitoring
   * @param {boolean} enabled - Whether to enable monitoring
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }
}

// Create singleton instance
const iconPerformanceMonitor = new IconPerformanceMonitor();

// Auto-log report every 30 seconds in development (disabled - was causing console spam)
// To re-enable, change `&& false` to `&& true`
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development' && false) {
  setInterval(() => {
    if (iconPerformanceMonitor.metrics.totalIcons > 0) {
      iconPerformanceMonitor.logReport();
    }
  }, 30000);
}

export default iconPerformanceMonitor;